import React, { useState, useCallback, useEffect, useMemo } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import "./CropModal.css";

/* ===============================
   HELPER: CREATE PREVIEW IMAGE
   =============================== */
const getCroppedPreview = async (imageSrc, crop, rotation = 0) => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise((res) => (image.onload = res));

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const size = Math.max(crop.width, crop.height);
    canvas.width = size;
    canvas.height = size;

    ctx.translate(size / 2, size / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-size / 2, -size / 2);

    ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        size,
        size
    );

    return canvas.toDataURL("image/png");
};

export default function CropModal({
    file,
    onCancel,
    onCropComplete,
    uploading = false,
}) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const [previewMode, setPreviewMode] = useState(false); // 👁 PREVIEW MODE
    const [previewUrl, setPreviewUrl] = useState(null);

    const imageURL = useMemo(() => {
        if (!file) return null;
        return URL.createObjectURL(file);
    }, [file]);

    useEffect(() => {
        return () => {
            if (imageURL) URL.revokeObjectURL(imageURL);
        };
    }, [imageURL]);

    const handleCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleReset = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setPreviewMode(false);
    };

    const handleRotate = () => {
        setRotation((prev) => (prev + 90) % 360);
    };

    const handlePreview = async () => {
        if (!croppedAreaPixels) return;

        const preview = await getCroppedPreview(
            imageURL,
            croppedAreaPixels,
            rotation
        );

        setPreviewUrl(preview);
        setPreviewMode(true);
    };

    return (
        <div className="crop-overlay">
            <div className="crop-box">

                <button className="crop-close" onClick={onCancel}>✖</button>

                <h2 className="crop-title">📸 Photo Crop Tool</h2>

                <p className="crop-subtitle">
                    {previewMode ? "Preview Result" : "Adjust, rotate, then preview"}
                </p>

                {/* ================= PREVIEW ================= */}
                {previewMode ? (
                    <div className="preview-container">
                        <img
                            src={previewUrl}
                            alt="Preview"
                            className="preview-image"
                        />
                    </div>
                ) : (
                    <>
                        <div className="crop-container">
                            {imageURL && (
                                <Cropper
                                    image={imageURL}
                                    crop={crop}
                                    zoom={zoom}
                                    rotation={rotation}
                                    aspect={1}
                                    cropShape="round"
                                    showGrid={false}
                                    onCropChange={setCrop}
                                    onZoomChange={(z) => setZoom(Number(z))}
                                    onCropComplete={handleCropComplete}
                                />
                            )}
                        </div>

                        <div className="zoom-label">
                            🔍 {zoom.toFixed(1)}x | 🔄 {rotation}°
                        </div>

                        <div className="crop-slider">
                            <input
                                type="range"
                                min={1}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={(e) =>
                                    setZoom(Number(e.target.value))
                                }
                            />
                        </div>

                        <button
                            className="crop-btn rotate"
                            onClick={handleRotate}
                        >
                            🔄 Rotate 90°
                        </button>
                    </>
                )}

                {/* ================= ACTIONS ================= */}
                <div className="crop-actions">
                    {previewMode ? (
                        <>
                            <button
                                className="crop-btn cancel"
                                onClick={() => setPreviewMode(false)}
                            >
                                Back
                            </button>

                            <button
                                className="crop-btn done"
                                onClick={() =>
                                    onCropComplete(
                                        file,
                                        croppedAreaPixels,
                                        rotation
                                    )
                                }
                            >
                                Confirm
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="crop-btn cancel" onClick={onCancel}>
                                Cancel
                            </button>

                            <button
                                className="crop-btn reset"
                                onClick={handleReset}
                            >
                                Reset
                            </button>

                            <button
                                className="crop-btn preview"
                                disabled={!croppedAreaPixels}
                                onClick={handlePreview}
                            >
                                👁 Submit
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
